import "./Register.scss";
import "../../components/GoogleSignIn/GoogleSignIn.scss";
import React, { useCallback, useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import axios from 'axios';
import Button from '../../components/Button/Button';
import GoogleSignIn from '../../components/GoogleSignIn/GoogleSignIn';
import AuthContext from '../../context/AuthContext';
import SnackbarContext from '../../context/SnackbarContext';
import { API_BASE_URL, GOOGLE_CLIENT_ID } from '../../config';

export default function Register() {

    const navigate = useNavigate();
    const { googleLogin } = useContext(AuthContext);
    const { setSnackbar } = useContext(SnackbarContext);
    const [submitting, setSubmitting] = useState(false);
    const [pendingEmail, setPendingEmail] = useState(null);
    const [resending, setResending] = useState(false);

    const { register, handleSubmit, getValues, formState: { errors } } = useForm();

    const onSubmit = async (data) => {
        setSubmitting(true);
        try {
            const r = await axios.post(`${API_BASE_URL}/api/users/create-user/`, {
                username: data.username,
                email: data.email,
                password: data.password,
            });
            setPendingEmail(r.data?.email || data.email);
        } catch (err) {
            const detail = err.response?.data;
            const msg = detail?.detail || detail?.username?.[0] || detail?.email?.[0] || detail?.password?.[0] || 'تعذر إنشاء الحساب';
            setSnackbar({ content: msg, success: false });
        } finally {
            setSubmitting(false);
        }
    };

    const onGoogleCredential = useCallback(async (credential) => {
        const r = await googleLogin(credential);
        if (r?.status === 200) {
            setSnackbar({ content: 'تم تسجيل الدخول عبر Google', success: true });
            navigate('/profile?welcome=1');
        } else {
            const msg = r?.data?.detail || 'تعذر تسجيل الدخول عبر Google';
            setSnackbar({ content: msg, success: false });
        }
    }, [googleLogin, navigate, setSnackbar]);

    const resend = async () => {
        if (!pendingEmail) return;
        setResending(true);
        try {
            await axios.post(`${API_BASE_URL}/api/users/resend-verification/`, { email: pendingEmail });
            setSnackbar({ content: 'تم إرسال رابط جديد إلى بريدك', success: true });
        } catch {
            setSnackbar({ content: 'تعذر إرسال البريد', success: false });
        } finally {
            setResending(false);
        }
    };

    return (
        <div className="register-background">
            <div className="register-container">

                <Link to="/" style={{ textDecoration: 'none' }}>
                    <div className="register-header">الصفحة الرئيسية</div>
                </Link>

                {pendingEmail ? (
                    <div className='register-pending'>
                        <h2>تحقق من بريدك الإلكتروني</h2>
                        <p>
                            أرسلنا رابط التفعيل إلى <strong>{pendingEmail}</strong>.
                            افتح الرسالة واضغط الرابط لتفعيل حسابك.
                        </p>
                        <button
                            type='button'
                            className='register-pending-resend'
                            onClick={resend}
                            disabled={resending}
                        >
                            {resending ? 'جارٍ الإرسال…' : 'لم تصلني — أعد الإرسال'}
                        </button>
                        <Link to='/' className='register-pending-home'>العودة للصفحة الرئيسية</Link>
                    </div>
                ) : (
                    <>
                        <div className="register-title">إنشاء حساب جديد</div>

                        {GOOGLE_CLIENT_ID && (
                            <div className='register-google'>
                                <GoogleSignIn text='signup_with' onCredential={onGoogleCredential} />
                                <div className='auth-divider'>أو</div>
                            </div>
                        )}

                        <form className="register-form" onSubmit={handleSubmit(onSubmit)}>
                            <input {...register("username", { required: true })} type="text" placeholder="اسم المستخدم" autoComplete='username' />
                            <input {...register("email", { required: true })} type="email" placeholder="البريد الإلكتروني" autoComplete='email' />
                            <input {...register("password", { required: true, minLength: 6 })} type="password" placeholder="كلمة المرور (٦ أحرف على الأقل)" autoComplete='new-password' />
                            <input
                                {...register("confirmPassword", {
                                    required: 'يرجى إعادة إدخال كلمة المرور',
                                    validate: (value) => value === getValues('password') || 'كلمتا المرور غير متطابقتين',
                                })}
                                type="password"
                                placeholder="تأكيد كلمة المرور"
                                autoComplete='new-password'
                            />
                            {errors.confirmPassword && (
                                <span className='register-field-error'>{errors.confirmPassword.message}</span>
                            )}
                            <Button placeholder={submitting ? "جارٍ الإنشاء…" : "تسجيل"} disabled={submitting} />
                        </form>

                        <div className="register-footer">
                            عندك حساب؟ <Link id="register-to-login" to="/">تسجيل الدخول</Link>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
